import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { FiMail, FiClock, FiAlertTriangle, FiRefreshCw, FiUser } from 'react-icons/fi';

/**
 * Stalled Signups panel for the super-admin dashboard.
 *
 * Two sections:
 *   Recoverable — pending_registrations still within the 24h window but
 *                 abandoned for >= 30min. These are warm leads.
 *   Lapsed      — past expiry, last 30d. Historical view of funnel drop.
 *
 * "Email them" buttons hand off to the mass-email composer via
 * sessionStorage (read by AdminEmails on mount). No navigation state
 * manipulation because the composer may be a separate bundle.
 */
const StalledSignups = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ recoverable: [], lapsed: [], counts: { recoverable: 0, lapsed: 0 } });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('recoverable');

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/admin/pending-registrations');
      if (response.data.success) {
        setData(response.data);
      }
    } catch (error) {
      console.error('Error fetching pending registrations:', error);
      toast.error('Failed to load stalled signups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const emailThem = (reg) => {
    // Hand off pre-populated recipient + starter template to the composer.
    // The composer reads this key on mount, then clears it.
    const payload = {
      recipients: [{ email: reg.email, firstName: reg.firstName, lastName: reg.lastName }],
      subject: `Finish setting up your LeaveWise account, ${reg.firstName}`,
      body: `Hi ${reg.firstName},

I noticed you started signing up for LeaveWise (${reg.plan} plan) but didn't finish at the payment step.

Is there something I can help with? Happy to walk you through it or answer any questions.

You can pick up where you left off here: https://leavewise.cloud/signup

— Dean
LeaveWise`,
      source: 'stalled_signup',
      sourceId: reg.id
    };
    try {
      sessionStorage.setItem('leavewise.emailCompose.prefill', JSON.stringify(payload));
    } catch (_) {}
    navigate('/admin/emails');
  };

  const rows = tab === 'recoverable' ? data.recoverable : data.lapsed;

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FiAlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Stalled Signups</h2>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
        >
          <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="flex border-b border-gray-200 dark:border-gray-700 px-4">
        <button
          onClick={() => setTab('recoverable')}
          className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            tab === 'recoverable'
              ? 'border-primary-600 text-primary-600 dark:text-primary-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Recoverable
          <span className="ml-1.5 inline-flex items-center justify-center px-1.5 py-0 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
            {data.counts.recoverable}
          </span>
        </button>
        <button
          onClick={() => setTab('lapsed')}
          className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            tab === 'lapsed'
              ? 'border-primary-600 text-primary-600 dark:text-primary-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Lapsed (30d)
          <span className="ml-1.5 inline-flex items-center justify-center px-1.5 py-0 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
            {data.counts.lapsed}
          </span>
        </button>
      </div>

      {loading ? (
        <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 mx-auto mb-2"></div>
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-6 text-center">
          <FiUser className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {tab === 'recoverable'
              ? 'No signups in progress right now — you\'re all caught up.'
              : 'No lapsed signups in the last 30 days.'}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
          {rows.map(reg => (
            <div key={reg.id} className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {reg.firstName} {reg.lastName}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {reg.email}
                  </span>
                  <span className="inline-flex items-center px-1.5 py-0 rounded text-[10px] font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 capitalize">
                    {reg.plan}
                  </span>
                </div>
                <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  <FiClock className="w-3 h-3" />
                  <span>Started {formatDistanceToNow(new Date(reg.createdAt), { addSuffix: true })}</span>
                  {tab === 'recoverable' && reg.expiresAt && (
                    <span className="ml-1">
                      · expires {formatDistanceToNow(new Date(reg.expiresAt), { addSuffix: true })}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => emailThem(reg)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-xs font-medium transition-colors flex-shrink-0"
                title="Compose a follow-up email"
              >
                <FiMail className="w-3.5 h-3.5" />
                Email them
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StalledSignups;
